import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DateOfBirthInput from "../inputs/DateOfBirthInput";
import TextInput from "../inputs/TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import FormSection from "./FormSection";
import useIdentityMessages from "./useIdentityMessages";

/**
 * Journey C's identity step: a student being registered, who does not have a
 * student number yet.
 *
 * ⛔ THERE IS NO MATRICULE INPUT IN THIS FILE, and that is the contract rather
 * than a layout choice. The server REJECTS a registration payment that carries
 * a matricule, so the field must not exist -- hiding one and leaving the value
 * in the journey would send it. Nothing here reads or writes
 * `schoolFeeContext.matricule`, which is what keeps the payload free of it.
 *
 * ⚠ Payer-declared, like journey B: this is what the payer says about the
 * student, not something the school has confirmed.
 */
const RegistrationIdentityForm = () => {
  const { t } = useTranslation();
  const {
    selectionId,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
  } = useSchoolFee();

  // No student number exists on registration, so its message never shows.
  const messages = useIdentityMessages({
    matricule: "",
    firstName,
    lastName,
    dateOfBirth,
  });

  if (selectionId.trim() === "") return null;

  return (
    <Box data-testid="school-fee-registration-identity">
      <FormSection title={t("schoolfee.registration.title")}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }} mb={2}>
          {t("schoolfee.registration.hint")}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextInput
              fullWidth
              required
              type="text"
              id="schoolfee-registration-first-name"
              label={t("schoolfee.student.first_name")}
              value={firstName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFirstName(event.target.value)
              }
              variant="outlined"
              {...messages.firstName}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput
              fullWidth
              required
              type="text"
              id="schoolfee-registration-last-name"
              label={t("schoolfee.student.last_name")}
              value={lastName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setLastName(event.target.value)
              }
              variant="outlined"
              {...messages.lastName}
            />
          </Grid>

          <Grid item xs={12}>
            <DateOfBirthInput
              required
              id="schoolfee-registration-dob"
              label={t("schoolfee.student.date_of_birth")}
              value={dateOfBirth}
              onChange={setDateOfBirth}
              {...messages.dateOfBirth}
            />
          </Grid>
        </Grid>
      </FormSection>
    </Box>
  );
};

export default RegistrationIdentityForm;
